import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Shield, Zap, Heart } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { useTheme } from '../contexts/ThemeContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Sun, Moon } from 'lucide-react';
import { HealthCrossLogoLarge } from '../components/HealthCrossLogo';

export const Landing = () => {
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const { t } = useLanguage();

  const features = [
    {
      icon: Zap,
      title: t('quickAssessment'),
      description: t('quickAssessmentDesc')
    },
    {
      icon: Shield,
      title: t('symptomMatching'),
      description: t('symptomMatchingDesc')
    },
    {
      icon: Heart,
      title: t('trackHistory'),
      description: t('trackHistoryDesc')
    }
  ];

  return (
    <div className="min-h-screen bg-background relative overflow-hidden">
      {/* Background glow */}
      <div className="absolute inset-0 hero-glow pointer-events-none" />
      
      {/* Theme toggle */}
      <div className="absolute top-6 right-6 z-10">
        <Button
          variant="ghost"
          size="icon"
          onClick={toggleTheme}
          className="rounded-full"
          data-testid="landing-theme-toggle"
        >
          {theme === 'light' ? <Moon className="w-5 h-5" /> : <Sun className="w-5 h-5" />}
        </Button>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-16">
        {/* Hero section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-20"
        >
          <div className="flex justify-center mb-8">
            <motion.div
              whileHover={{ scale: 1.1 }}
              className="animate-pulse-glow rounded-full"
            >
              <HealthCrossLogoLarge />
            </motion.div>
          </div>
          
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight mb-6">
            {t('heroTitle')}
            <br />
            <span className="text-primary">{t('heroTitleHighlight')}</span>
          </h1>
          
          <p className="text-base sm:text-lg text-muted-foreground max-w-xl mx-auto mb-10">
            {t('heroDescription')}
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              size="lg"
              className="rounded-full px-8 btn-scale"
              onClick={() => navigate('/auth?mode=register')}
              data-testid="get-started-btn"
            >
              {t('getStarted')}
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            <Button
              variant="outline"
              size="lg"
              className="rounded-full px-8 btn-scale"
              onClick={() => navigate('/symptoms')}
              data-testid="try-now-btn"
            >
              {t('tryWithoutSignIn')}
            </Button>
          </div>
          
          <p className="text-sm text-muted-foreground mt-4">
            {t('noAccountNeeded')} •{' '}
            <span 
              onClick={() => navigate('/auth')} 
              className="text-primary hover:underline cursor-pointer font-medium"
            >
              {t('signInLink')}
            </span>{' '}
            {t('toSaveHistory')}
          </p>
        </motion.div>

        {/* Features */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="grid grid-cols-1 md:grid-cols-3 gap-6"
        >
          {features.map((feature, index) => (
            <motion.div
              key={feature.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.3 + index * 0.1 }}
            >
              <Card className="p-8 rounded-3xl border-2 border-border hover:border-primary/50 transition-all card-hover h-full">
                <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center mb-6">
                  <feature.icon className="w-7 h-7 text-primary" />
                </div>
                <h3 className="text-xl font-bold mb-3">{feature.title}</h3>
                <p className="text-muted-foreground">{feature.description}</p>
              </Card>
            </motion.div>
          ))}
        </motion.div>

        {/* Medical professionals CTA */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="mt-20"
        >
          <Card className="p-8 sm:p-12 rounded-3xl border-2 border-primary/30 bg-primary/5">
            <div className="text-center">
              <span className="label-medical text-primary">{t('forHealthcareProviders')}</span>
              <h2 className="text-2xl sm:text-3xl font-bold mt-4 mb-4">
                {t('areYouMedicalProfessional')}
              </h2>
              <p className="text-muted-foreground mb-8 max-w-lg mx-auto">
                {t('medicalProfessionalDescription')}
              </p>
              <Button
                variant="outline"
                size="lg"
                className="rounded-full px-8 border-primary text-primary hover:bg-primary hover:text-primary-foreground btn-scale"
                onClick={() => navigate('/auth?mode=register&role=medical')}
                data-testid="medical-signup-btn"
              >
                {t('joinAsProfessional')}
              </Button>
            </div>
          </Card>
        </motion.div>
      </div>
    </div>
  );
};
